import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardFooter,
  Media,
  Table,
  Container,
  Row,
  Col,
  UncontrolledTooltip,
  Input,
  FormGroup,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
// core components
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { serverHost } from "variables/serverdata.js";

function CompanySites() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "company_name",
    direction: "asc",
  });
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [allSites, setAllSites] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchSites();
  }, []);

  // Reset pagination when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedClientId]);

  // Filter sites based on selected client
  const filteredSites = selectedClientId
    ? allSites.filter(
      (site) => site.client?.client_uuid === selectedClientId
    )
    : allSites;

  const selectedSiteFromStorage = JSON.parse(
    localStorage.getItem("selectedSite")
  );

  const selectedSiteUuid = selectedSiteFromStorage?.site_uuid;

  const fetchSites = async () => {
    try {
      // Get current user's dealer_uuid from localStorage
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (!currentUser?.dealer_uuid) {
        console.error("No dealer_uuid found for current user");
        return;
      }

      let clientData = [];
      let siteData = [];

      console.log("Access Token:", currentUser.access_token);
      const ac_token = currentUser.access_token;
      // GLOBAL user can see all clients
      const client_fetch = await fetch(`${serverHost}/fetch_clients`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Access: `Bearer ${ac_token}`,
        },
      });
      const clients_json = await client_fetch.json();
      console.log("Fetched:", clients_json.data);
      clientData = clients_json.data || [];
      console.log("Client data:", clientData);
      // GLOBAL users can see all sites and sites for their dealer
      const sites_fetch = await fetch(`${serverHost}/fetch_sites`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Access: `Bearer ${ac_token}`,
        },
      });
      const sites_json = await sites_fetch.json();
      console.log("Fetched:", sites_json.data);
      siteData = sites_json.data || [];
      console.log("Site data:", siteData);

      // Store sites and clients
      setAllSites(siteData || []);
      setClients(clientData || []);
      setLoading(false);
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    }
  };

  const handleViewSite = (site) => {
    const siteCopy = structuredClone(site);

    localStorage.setItem(
      "addEditSite",
      JSON.stringify({
        ...siteCopy,
        isFromSitesList: true,
      })
    );

    navigate("/admin/siteprofile");
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedSites = [...allSites].sort((a, b) => {
      // Handle nested client.company_name
      if (key === "client.company_name") {
        const aVal = a.client?.company_name || "";
        const bVal = b.client?.company_name || "";
        if (aVal < bVal) return direction === "asc" ? -1 : 1;
        if (aVal > bVal) return direction === "asc" ? 1 : -1;
        return 0;
      }

      // Handle regular fields
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setAllSites(sortedSites);
  };

  const handleDeleteSite = async () => {
    if (!siteToDelete) return;

    try {
      const url = `${serverHost}/delete_site`;
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          site_uuid: siteToDelete.site_uuid,
          client_uuid: siteToDelete.client.client_uuid,
        }),
      });
      if (!resp.ok) {
        const errorData = await resp.json();
        console.error(`Deletion failed ${resp.status}: ${errorData}`);
        return;
      }

      // Remove the deleted site from the state
      setAllSites(
        allSites.filter((site) => site.site_uuid !== siteToDelete.site_uuid)
      );
      setDeleteModal(false);
      setSiteToDelete(null);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const toggleDeleteModal = (site = null) => {
    setDeleteModal(!deleteModal);
    setSiteToDelete(site);
  };

  const handleNewSite = () => {
    // Store an empty site object with isNew flag
    localStorage.setItem(
      "addEditSite",
      JSON.stringify({
        company_name: "",
        address: "",
        city: "",
        country: "",
        postal_code: "",
        client_uuid: "",
        isNew: true,
        isFromSitesList: true,
      })
    );
    // Navigate to the site profile page
    navigate("/admin/siteprofile");
  };

  

  return (
    <>
      <SimpleHeader name="Company Sites" parentName="My Account" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader
                style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}
                className="border-0"
              >
                <Row className="align-items-center">
                  <Col xs="6" className="d-flex align-items-center">
                    <h3 className="mb-0">Company Sites</h3>
                    {JSON.parse(localStorage.getItem("user"))?.user_role ===
                      "GLOBAL" && (
                        <UncontrolledDropdown className="ml-2">
                          <DropdownToggle
                            size="sm"
                            color="primary"
                            id="filter-tooltip"
                          >
                            <i className="fas fa-filter mr-2" />
                            <span className="font-weight-bold">
                              {selectedClientId
                                ? clients.find(
                                  (c) => c.client_uuid === selectedClientId
                                )?.company_name
                                : "All"}
                            </span>
                          </DropdownToggle>
                          <UncontrolledTooltip
                            placement="right"
                            target="filter-tooltip"
                          >
                            Filter by Client
                          </UncontrolledTooltip>

                          <DropdownMenu
                            style={{
                              backgroundColor: isDark ? "#1f2937" : "#fff",
                              color: isDark ? "#e5e7eb" : "#212529",
                            }}
                          >
                            <DropdownItem
                              header
                              style={{
                                color: isDark ? "#9ca3af" : "#6c757d",
                              }}
                            >
                              Filter by Client
                            </DropdownItem>

                            <DropdownItem
                              active={!selectedClientId}
                              onClick={() => setSelectedClientId("")}
                              style={{
                                color: isDark ? "#e5e7eb" : "#212529",
                                backgroundColor: isDark && !selectedClientId ? "#2563eb" : "transparent",
                              }}
                              onMouseEnter={(e) => {
                                if (isDark) {
                                  e.currentTarget.style.backgroundColor = "#3b82f6";
                                  e.currentTarget.style.color = "#ffffff";
                                } else {
                                  e.currentTarget.style.backgroundColor = "#e2e8f0"; // light hover
                                  e.currentTarget.style.color = "#111827";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (isDark) {
                                  e.currentTarget.style.backgroundColor = !selectedClientId ? "#2563eb" : "transparent";
                                  e.currentTarget.style.color = "#e5e7eb";
                                } else {
                                  e.currentTarget.style.backgroundColor = "transparent";
                                  e.currentTarget.style.color = "#212529";
                                }
                              }}
                            >
                              All Clients
                            </DropdownItem>

                            {clients.map((client) => (
                              <DropdownItem
                                key={client.client_uuid}
                                active={selectedClientId === client.client_uuid}
                                onClick={() => setSelectedClientId(client.client_uuid)}
                                style={{
                                  color: isDark ? "#e5e7eb" : "#212529",
                                  backgroundColor:
                                    isDark && selectedClientId === client.client_uuid ? "#2563eb" : "transparent",
                                }}
                                onMouseEnter={(e) => {
                                  if (isDark) {
                                    e.currentTarget.style.backgroundColor = "#3b82f6"; // bright hover in dark mode
                                    e.currentTarget.style.color = "#ffffff";
                                  } else {
                                    e.currentTarget.style.backgroundColor = "#e2e8f0"; // light hover in light mode
                                    e.currentTarget.style.color = "#111827"; // darker text for contrast
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (isDark) {
                                    e.currentTarget.style.backgroundColor =
                                      selectedClientId === client.client_uuid ? "#2563eb" : "transparent";
                                    e.currentTarget.style.color = "#e5e7eb";
                                  } else {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                    e.currentTarget.style.color = "#212529";
                                  }
                                }}
                              >
                                {client.company_name}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      )}
                  </Col>
                  <Col className="text-right" xs="6">
                    <Button
                      className="btn-round btn-icon"
                      color="info"
                      onClick={() => {navigate("/admin/site-wizard")}}
                      size="sm"
                    >
                      <span className="btn-inner--icon mr-1">
                        <i class="fas fa-magic"  />
                      </span>
                      <span className="btn-inner--text">Quick Setup</span>
                    </Button>
                    <Button
                      className="btn-round btn-icon"
                      color="info"
                      onClick={handleNewSite}
                      size="sm"
                    >
                      <span className="btn-inner--icon mr-1">
                        <i className="fas fa-building" />
                      </span>
                      <span className="btn-inner--text">New</span>
                    </Button>
                  </Col>
                </Row>
              </CardHeader>

              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th
                      className="sort"
                      data-sort="name"
                      scope="col"
                      onClick={() => handleSort("company_name")}
                      style={{ cursor: "pointer" }}
                    >
                      Site Name
                    </th>
                    <th
                      className="sort"
                      data-sort="client"
                      scope="col"
                      onClick={() => handleSort("client.company_name")}
                      style={{ cursor: "pointer" }}
                    >
                      Client
                    </th>
                    <th
                      className="sort"
                      data-sort="address"
                      scope="col"
                      onClick={() => handleSort("address")}
                      style={{ cursor: "pointer" }}
                    >
                      Address
                    </th>
                    <th
                      className="sort"
                      data-sort="city"
                      scope="col"
                      onClick={() => handleSort("city")}
                      style={{ cursor: "pointer" }}
                    >
                      City
                    </th>
                    <th
                      className="sort"
                      data-sort="country"
                      scope="col"
                      onClick={() => handleSort("country")}
                      style={{ cursor: "pointer" }}
                    >
                      Country
                    </th>
                    <th
                      className="sort"
                      data-sort="created"
                      scope="col"
                      onClick={() => handleSort("created_at")}
                      style={{ cursor: "pointer" }}
                    >
                      Created At
                    </th>
                    <th scope="col" />
                  </tr>
                </thead>
                <tbody className="list">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        Loading sites...
                      </td>
                    </tr>
                  ) : filteredSites.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No sites found
                      </td>
                    </tr>
                  ) : (
                    filteredSites
                      .slice(
                        (currentPage - 1) * itemsPerPage,
                        currentPage * itemsPerPage
                      )
                      .map((site) => {
                        const isDeleteDisabled = site.site_uuid === selectedSiteUuid;

                        return (
                          <tr
                            key={site.site_uuid}
                            onClick={() => handleViewSite(site)}
                            style={{ cursor: "pointer" }}
                            className="hover-bg-light"
                          >
                            <td scope="row">
                              <Media className="align-items-center">
                                <a
                                  className="avatar rounded-circle mr-3"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <img
                                    alt="..."
                                    src={require("assets/img/theme/address-site.jpg")}
                                  />
                                </a>
                                <Media>
                                  <span className="name mb-0 text-sm">
                                    {site.company_name}
                                  </span>
                                </Media>
                              </Media>
                            </td>
                            <td className="client">
                              {site.client?.company_name || "-"}
                            </td>
                            <td className="address">{site.address}</td>
                            <td className="city">{site.city}</td>
                            <td className="country">{site.country}</td>
                            <td className="created">
                              {site.created_at
                                ? new Date(site.created_at)
                                  .toISOString()
                                  .slice(0, 10)
                                : "N/A"}
                            </td>
                            <td className="table-actions">
                              <a
                                className="table-action table-action-delete"
                                href="#pablo"
                                id={`tooltip-delete-${site.site_uuid}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();

                                  if (isDeleteDisabled) return;

                                  toggleDeleteModal(site);
                                }}
                                style={{
                                  opacity: isDeleteDisabled ? 0.4 : 1,
                                  cursor: isDeleteDisabled ? "not-allowed" : "pointer",
                                }}
                              >
                                <i className="fas fa-trash fa-lg" />
                              </a>

                              <UncontrolledTooltip
                                delay={0}
                                target={`tooltip-delete-${site.site_uuid}`}
                              >
                                {isDeleteDisabled
                                  ? "Cannot delete the currently selected site"
                                  : "Delete Site"}
                              </UncontrolledTooltip>
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </Table>

              <CardFooter
                style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}
                className="py-4"
              >
                <nav aria-label="...">
                  <Pagination
                    className="pagination justify-content-end mb-0"
                    listClassName="justify-content-end mb-0"
                  >
                    <PaginationItem disabled={currentPage === 1}>
                      <PaginationLink
                        href="#pablo"
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentPage > 1) setCurrentPage(currentPage - 1);
                        }}
                        tabIndex="-1"
                      >
                        <i className="fas fa-angle-left" />
                        <span className="sr-only">Previous</span>
                      </PaginationLink>
                    </PaginationItem>
                    {[
                      ...Array(Math.ceil(filteredSites.length / itemsPerPage)),
                    ].map((_, index) => (
                      <PaginationItem
                        active={currentPage === index + 1}
                        key={index}
                      >
                        <PaginationLink
                          href="#pablo"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(index + 1);
                          }}
                        >
                          {index + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem
                      disabled={
                        currentPage ===
                        Math.ceil(filteredSites.length / itemsPerPage)
                      }
                    >
                      <PaginationLink
                        href="#pablo"
                        onClick={(e) => {
                          e.preventDefault();
                          if (
                            currentPage <
                            Math.ceil(filteredSites.length / itemsPerPage)
                          )
                            setCurrentPage(currentPage + 1);
                        }}
                      >
                        <i className="fas fa-angle-right" />
                        <span className="sr-only">Next</span>
                      </PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </nav>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>
      <Modal
        isOpen={deleteModal}
        toggle={toggleDeleteModal}
        centered
      >
        <ModalHeader
          toggle={toggleDeleteModal}
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
            color: isDark ? "#e2e8f0" : "#212529",
            borderBottom: "1px solid",
            borderColor: isDark ? "#374151" : "#dee2e6",
          }}
        >
          Delete Site
        </ModalHeader>

        <ModalBody
          style={{
            backgroundColor: isDark ? "#111827" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          <div className="text-center">
            <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger" />

            <p className="mb-2">
              Are you sure you want to delete <span style={{fontWeight: "bold"}}>{siteToDelete?.company_name}</span>?
            </p>

            <strong>This action cannot be undone.</strong>

            <p className="text-sm text-black mb-0">
              All site data will be
              permanently deleted, including:
            </p>

            <ul className="text-left text-sm text-muted mt-3">
              <li>Site configuration and settings</li>
              <li>All associated modules and their data</li>
              <li>User access and permissions for this site</li>
              <li>Historical records and logs</li>
            </ul>
          </div>
        </ModalBody>

        <ModalFooter
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
            borderTop: "1px solid",
            borderColor: isDark ? "#374151" : "#dee2e6",
          }}
        >
          <Button color="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteSite}>
            Delete Site
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default CompanySites;
